/** @typedef {import("../api/types").Notification} Notification */
import React, {useCallback, useEffect, useState} from "react";
import axios from "axios";
import {Link, useNavigate} from "react-router-dom";
import {Badge, Button, Dropdown, DropdownMenu, DropdownToggle, Spinner} from "reactstrap";
import {BellIcon} from "../shared/components/icons";
import {formatAgo} from "../shared/utils/format";
import {notificationText, notificationsChanged, onNotificationsChanged} from "../shared/utils/notifications";
import "./NotificationBell.css";

const URL = "/os/api/notifications/";
const POLL_MS = 60 * 1000;
const LATEST = 5;
// Bootstrap's md breakpoint: below it the navbar is the burger menu.
const PHONE = "(max-width: 767.98px)";

/**
 * The bell: how many things the customer or the system did to the provider's
 * orders are still unread (confirmed, canceled, expired), and the latest few
 * when it is opened. The count is refreshed every minute while the tab is in
 * view, when it comes back into view, and when an order is opened. On a phone
 * there is no room for the list to open: the bell goes to the full page.
 *
 * @param {{onNavigate?: () => void}} props `onNavigate` runs when the bell takes
 * the phone to the page (e.g. to close the burger menu).
 */
const NotificationBell = ({onNavigate}) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(0);
    const [items, setItems] = useState(/** @type {Notification[]|null} */ (null));
    const [failed, setFailed] = useState(false);
    const [busy, setBusy] = useState(false);

    const loadCount = useCallback(async () => {
        try {
            const {data} = await axios.get(`${URL}unread_count/`);
            setCount(data.count);
        } catch {
            // The badge keeps what it had; the next refresh tries again.
        }
    }, []);

    const loadItems = useCallback(async () => {
        setFailed(false);
        try {
            const {data} = await axios.get(URL, {params: {page_size: LATEST}});
            setItems(data.results);
        } catch {
            setFailed(true);
        }
    }, []);

    useEffect(() => {
        loadCount();
        const visible = () => document.visibilityState === "visible";
        const timer = setInterval(() => visible() && loadCount(), POLL_MS);
        const back = () => visible() && loadCount();
        document.addEventListener("visibilitychange", back);
        const stop = onNotificationsChanged(() => {
            loadCount();
        });
        return () => {
            clearInterval(timer);
            document.removeEventListener("visibilitychange", back);
            stop();
        };
    }, [loadCount]);

    // Opening it shows the latest, fresh.
    useEffect(() => {
        if (open) loadItems();
    }, [open, loadItems]);

    const markAllRead = async () => {
        setBusy(true);
        try {
            await axios.post(`${URL}read_all/`);
            notificationsChanged();
            await loadItems();
        } catch {
            setFailed(true);
        } finally {
            setBusy(false);
        }
    };

    const toggle = () => {
        if (window.matchMedia?.(PHONE).matches) {
            onNavigate?.();
            navigate("/notifications");
            return;
        }
        setOpen((value) => !value);
    };

    return (
        <Dropdown nav inNavbar isOpen={open} toggle={toggle}>
            <DropdownToggle nav className="notification-bell" aria-label={count ? `Avisos (${count} novos)` : "Avisos"}>
                <BellIcon size={20} />
                {/* in the open mobile menu a lone icon reads as a stray mark */}
                <span className="d-md-none ms-2">Avisos</span>
                {count > 0 && (
                    <Badge pill color="danger" className="notification-bell__badge">
                        {count > 99 ? "99+" : count}
                    </Badge>
                )}
            </DropdownToggle>
            <DropdownMenu end className="notification-bell__menu">
                <div className="d-flex align-items-center justify-content-between px-3 py-2">
                    <strong>Avisos</strong>
                    <Button color="link" size="sm" className="p-0" disabled={busy || count === 0} onClick={markAllRead}>
                        Marcar todos como lidos
                    </Button>
                </div>
                <div className="dropdown-divider my-0" />
                {failed && <div className="px-3 py-3 text-danger small">Não foi possível carregar os avisos.</div>}
                {!failed && items === null && (
                    <div className="px-3 py-3 text-center">
                        <Spinner size="sm" />
                    </div>
                )}
                {!failed && items?.length === 0 && <div className="px-3 py-3 text-muted small">Nenhum aviso.</div>}
                {!failed &&
                    items?.map((item) => (
                        <Link
                            key={item.id}
                            to={`/orders/${item.order_id}`}
                            state={{backTo: "/notifications"}}
                            className={
                                "notification-bell__item" + (item.read ? "" : " notification-bell__item--unread")
                            }
                            onClick={() => setOpen(false)}
                        >
                            <span className="d-block">{notificationText(item)}</span>
                            <span className="d-block small text-muted">{formatAgo(item.created_at)}</span>
                        </Link>
                    ))}
                <Link to="/notifications" className="notification-bell__all" onClick={() => setOpen(false)}>
                    Ver todos
                </Link>
            </DropdownMenu>
        </Dropdown>
    );
};

export default NotificationBell;
